import React, { useState } from 'react';
import { Button, Form } from 'antd';
import {
  Link,
  BaseButton,
  ButtonGroup,
  Drawer,
  ErrorHandler,
  Icon,
} from '@common/components';
import { Colors, FontSize, FontWeight, LineHeight } from '@common/tokens';
import { SupplierStatusTypes } from '@common/graphql';
import { useApolloClient, useMutation, useQuery } from '@common/hooks';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  CreateOrganizationSupplierAttachmentMutation,
  UpdateEnabledRolesForSupplierMutation,
  UpdateSupplierTierMutation,
  SUPPLIER_QUERY,
} from '@institutions/graphql';
import {
  SuppliersManagementViewDrawerContent,
  tabKeys,
  TabKeysType,
} from './_suppliers-management-view-drawer-content';
import { Store } from 'antd/lib/form/interface';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { buildDialogUrl } from '@common/services/dialogs-service';

export function SuppliersManagementViewDrawer({
  open,
  onClose,
  supplierId,
}: {
  open: boolean;
  onClose: () => void;
  supplierId?: string;
}) {
  const apolloClient = useApolloClient();

  const [currentTabKey, setCurrentTabKey] = useState<TabKeysType>('details');
  const tierEditionState = useState(false);
  const [tierEditionMode, setTierEditionMode] = tierEditionState;
  const [filesEditionMode, setFilesEditionMode] = useState(false);
  const [rolesAccessEditionMode, setRolesAccessEditionMode] = useState(false);
  const [form] = Form.useForm();

  const closeEditionMode = () => {
    setFilesEditionMode(false);
    setRolesAccessEditionMode(false);
    setTierEditionMode(false);
  };

  const errorTierMessage = () =>
    createMessage('error', 'There was an error when trying to update the tier');

  const [updateTier, { loading: updateTierLoading }] = useMutation(
    UpdateSupplierTierMutation,
    {
      onCompleted: ({ createOrganizationSupplierTier }) => {
        if (createOrganizationSupplierTier?.success) {
          closeEditionMode();
          createMessage('success', 'Tier was updated successfully');
        } else {
          errorTierMessage();
        }
      },
      onError: errorTierMessage,
      refetchQueries: ['SupplierTier', 'Tiers'],
    }
  );

  const [
    updateEnabledRolesForSupplier,
    { loading: updateEnabledRolesForSupplierLoading },
  ] = useMutation(UpdateEnabledRolesForSupplierMutation, {
    onCompleted: ({ updateEnabledRolesForSupplier }) => {
      if (updateEnabledRolesForSupplier) {
        closeEditionMode();
        createMessage('success', 'Roles are saved');
      } else {
        createMessage(
          'error',
          'There was an error when trying to update the roles'
        );
      }
    },
    onError: () =>
      createMessage(
        'error',
        'There was an error when trying to update the roles'
      ),
    refetchQueries: ['PaginatedOrganizationRoles', 'SupplierHistory'],
  });

  const errorFilesMessage = () =>
    createMessage('error', 'Failed to save file. Please try again.');

  const [createSupplierFile, { loading: createSupplierFileLoading }] =
    useMutation(CreateOrganizationSupplierAttachmentMutation, {
      onCompleted: ({ createOrganizationSupplierAttachment }) => {
        if (createOrganizationSupplierAttachment?.errors) {
          errorFilesMessage();
          addErrorsToForm(form, createOrganizationSupplierAttachment.errors);
        } else {
          closeEditionMode();
          createMessage('success', 'File saved!');
          form.resetFields();
          apolloClient.refetchQueries({
            include: ['OrganizationSupplierAttachments', 'SupplierHistory'],
          });
        }
      },
      onError: errorFilesMessage,
    });

  const { data, loading, error } = useQuery(SUPPLIER_QUERY, {
    skip: !supplierId,
    variables: {
      id: supplierId || '',
    },
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SuppliersManagementViewDrawer"
        messageType="contextual"
      />
    );
  }

  const supplier = data?.supplier;
  const status = supplier?.statusForCurrentOrganization?.status;
  const action = status && getActionByStatus(status, supplier.id);

  const showEditLocationButton =
    status === 'ACTIVE' && currentTabKey === tabKeys.locations;

  const onFormSubmit = (values: Store) => {
    if (!supplier) return;

    if (rolesAccessEditionMode) {
      updateEnabledRolesForSupplier({
        variables: {
          supplierId: supplier.id,
          roleIds: values.roleIds,
        },
      });
    }

    if (tierEditionMode) {
      updateTier({
        variables: {
          input: {
            tierId: values.selectedTierId,
            supplierId: supplier.id,
          },
        },
      });
    }

    if (currentTabKey === tabKeys.files) {
      createSupplierFile({
        variables: {
          file: values.file.file,
          supplierId: supplier.id,
          input: {
            fileName: values.fileName,
            expiresOn: values.expiresOn,
          },
        },
      });
    }
  };

  const filesTabFooter = filesEditionMode ? (
    <ButtonGroup>
      <Button
        size="large"
        onClick={() => {
          closeEditionMode();
          form.resetFields();
        }}
      >
        Back
      </Button>
      <Button
        type="primary"
        size="large"
        loading={createSupplierFileLoading}
        onClick={form.submit}
      >
        Save File
      </Button>
    </ButtonGroup>
  ) : (
    <BaseButton
      size="large"
      icon={<Icon icon="cloudArrowUp" kind="solid" />}
      onClick={() => setFilesEditionMode(true)}
    >
      Upload
    </BaseButton>
  );

  const rolesAccessTabFooter = rolesAccessEditionMode ? (
    <ButtonGroup>
      <Button
        size="large"
        onClick={() => {
          setRolesAccessEditionMode(false);
          form.resetFields();
        }}
      >
        Cancel
      </Button>
      <Button
        type="primary"
        size="large"
        loading={updateEnabledRolesForSupplierLoading}
        onClick={form.submit}
      >
        Save
      </Button>
    </ButtonGroup>
  ) : (
    <BaseButton size="large" onClick={() => setRolesAccessEditionMode(true)}>
      Edit Roles Access
    </BaseButton>
  );

  const TierTabFooter = () => (
    <ButtonGroup>
      <Button size="large" onClick={closeEditionMode}>
        Cancel
      </Button>
      <Button
        type="primary"
        size="large"
        loading={updateTierLoading}
        onClick={form.submit}
      >
        Save
      </Button>
    </ButtonGroup>
  );

  const detailsTabFooter = (action ||
    supplier?.canUninvite ||
    tierEditionMode) && (
    <ButtonGroup>
      {action && (
        <Link to={action.route}>
          <Button size="large" type={action.buttonStyle}>
            {action.label}
          </Button>
        </Link>
      )}
      {tierEditionMode && <TierTabFooter />}
    </ButtonGroup>
  );

  const locationsTabFooter = showEditLocationButton && (
    <Link to={`/works/admin/suppliers/locations-update/${supplier?.id}`}>
      <Button size="large">Edit Locations</Button>
    </Link>
  );

  const footerList = {
    details: detailsTabFooter,
    contacts: null,
    locations: locationsTabFooter,
    files: filesTabFooter,
    history: null,
    rolesAccess: rolesAccessTabFooter,
  };

  const onTabChange = (tab: TabKeysType) => {
    setCurrentTabKey(tab);
    closeEditionMode();
    form.resetFields();
  };

  return (
    <>
      <Drawer
        size="lg"
        open={open}
        className="admin-supplier-view-drawer"
        title={buildTitle(supplier?.name)}
        footer={footerList[currentTabKey]}
        onClose={() => {
          onClose();
          closeEditionMode();
          setCurrentTabKey('details');
        }}
        loading={loading || !supplier}
      >
        {supplier && (
          <SuppliersManagementViewDrawerContent
            form={form}
            onTabChange={onTabChange}
            onFormSubmit={onFormSubmit}
            tierEditionState={tierEditionState}
            filesEditionMode={filesEditionMode}
            rolesAccessEditionMode={rolesAccessEditionMode}
            supplier={supplier}
          />
        )}
      </Drawer>
      <style jsx>{`
        :global(.admin-supplier-view-drawer h3.ant-typography) {
          color: ${Colors.midnightLighter};
          font-weight: 400;
          font-size: ${FontSize.md};
          line-height: ${LineHeight.lg};
          margin-bottom: 20px;
        }
        :global(.admin-supplier-view-drawer h4.ant-typography) {
          font-weight: ${FontWeight.medium};
          font-size: ${FontSize.xl};
          line-height: ${LineHeight.lg};
          margin-bottom: 20px;
        }
      `}</style>
    </>
  );
}

function buildTitle(name?: string) {
  if (!name) {
    return "Supplier's Info";
  }

  const lastLetter = name[name.length - 1];
  const isLastLetterS = lastLetter.toUpperCase() === 'S';

  return `${name}'${isLastLetterS ? '' : 's'} Info`;
}

function getActionByStatus(status: SupplierStatusTypes, supplierId: string) {
  return {
    AVAILABLE: {
      label: 'Invite Supplier',
      buttonStyle: 'primary' as const,
      route: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/invitation/${supplierId}`,
    },
    DECLINED: {
      label: 'Reinvite',
      buttonStyle: 'default' as const,
      route: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/invitation/${supplierId}`,
    },
    PENDING_ACTIVATION: {
      label: 'Activate',
      buttonStyle: 'primary' as const,
      route: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/activation/${supplierId}`,
    },
    INVITED: {
      label: 'Reinvite',
      buttonStyle: 'default' as const,
      route: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/invitation/${supplierId}`,
    },
    PENDING_SIGNUP: {
      label: 'Reinvite',
      buttonStyle: 'default' as const,
      route: buildDialogUrl('suppliers-management-reinvite-drawer', {
        props: {
          supplierId,
        },
      }),
    },
    ACTIVE: null,
  }[status];
}
